import type { MouseEvent } from "react";
import { RedirectionUrls, RedirectStatus } from "./ClientRedirect.types";
import { OpenAppButton } from "./OpenAppButton";
import { RedirectAlreadyRedirected } from "./RedirectAlreadyRedirected";
import { RedirectAppOpenAttempted } from "./RedirectAppOpenAttempted";
import { RedirectAppOpenOnTap } from "./RedirectAppOpenOnTap";
import { RedirectDestinationActions } from "./RedirectDestinationActions";
import { RedirectFailed } from "./RedirectFailed";
import { RedirectSpinner } from "./RedirectSpinner";

type RedirectStatusContentProps = {
  status: RedirectStatus | null;
  os: string;
  redirectionUrls: RedirectionUrls;
  onStoreTap?: (event: MouseEvent<HTMLAnchorElement>) => void;
};

function RedirectStatusMessage({
  status,
  os,
  iosUrl,
}: {
  status: RedirectStatus;
  os: string;
  iosUrl: string | null;
}) {
  switch (status) {
    case "alreadyRedirected":
      return <RedirectAlreadyRedirected os={os} iosUrl={iosUrl} />;
    case "appOpenAttempted":
      return <RedirectAppOpenAttempted os={os} />;
    case "appOpenOnTap":
      return <RedirectAppOpenOnTap />;
    case "failed":
      return <RedirectFailed />;
    default: {
      const unhandled: never = status;
      return unhandled;
    }
  }
}

/**
 * What the fallback layout shows once the automatic redirect is out of the
 * picture — still in flight, already used, failed, or replaced by an attempt
 * to open the app.
 */
export function RedirectStatusContent({
  status,
  os,
  redirectionUrls,
  onStoreTap,
}: RedirectStatusContentProps) {
  if (!status) return <RedirectSpinner />;

  return (
    <div className="flex flex-col items-center gap-4">
      <RedirectStatusMessage
        status={status}
        os={os}
        iosUrl={redirectionUrls.ios}
      />
      {status === "appOpenOnTap" && os === "ios" && redirectionUrls.ios ? (
        <OpenAppButton iosUrl={redirectionUrls.ios} />
      ) : (
        <RedirectDestinationActions
          os={os}
          iosUrl={redirectionUrls.ios}
          androidUrl={redirectionUrls.android}
          webUrl={redirectionUrls.web}
          onStoreTap={onStoreTap}
        />
      )}
    </div>
  );
}
